// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Buffer Calculator
// Subtracts LEGITIMATE internal consumption from raw gaps before flagging
// them as financial leaks. This is what the audit called "Operational Buffers."
//
// Example:
//   Expected coffee usage: 85kg
//   Actual:                92kg
//   Raw gap:               -7kg (looks like 7kg theft)
//
//   But: 5 staff × 30 days × 25g allowance = 3.75kg staff consumption
//        + 2% waste tolerance on 85kg     = 1.70kg natural waste
//        = 5.45kg allowed buffer
//
//   TRUE gap: -1.55kg (very different story)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Apply consumption buffers to a raw gap.
 * @param {Object} params
 * @param {Object} params.gap              - the raw financial_gap row
 * @param {Array}  params.buffers          - consumption_buffers rows (tenant-scoped)
 * @param {Array}  params.employees        - used to count staff for per-staff allowances
 * @param {number} params.periodDays       - defaults to 30
 * @returns {Object} { ...gap, raw_gap, allowance, adjusted_gap, adjusted_loss, buffer_breakdown }
 */
export function applyBuffers({ gap, buffers = [], employees = [], periodDays = 30 }) {
  const rawGapAmount = Math.abs(parseFloat(gap.gap_amount || 0));
  const rawLoss = parseFloat(gap.estimated_monetary_loss || 0);
  const staffCount = employees.filter(e => e.is_active !== false).length;
  const breakdown = [];
  let totalAllowanceQty = 0;

  // Find relevant buffers for this gap
  const relevantBuffers = buffers.filter(b => {
    if (!b.is_active) return false;
    if (gap.ingredient_id && b.ingredient_id === gap.ingredient_id) return true;
    if (gap.product_id && b.product_id === gap.product_id) return true;
    return false;
  });

  for (const buffer of relevantBuffers) {
    let qty = 0;
    let reason_ar = '';
    let reason_en = '';

    if (buffer.allowance_per_staff_per_day > 0) {
      qty = parseFloat(buffer.allowance_per_staff_per_day) * staffCount * periodDays;
      reason_ar = `استهلاك الموظفين: ${staffCount} موظف × ${buffer.allowance_per_staff_per_day} ${buffer.unit}/يوم × ${periodDays} يوم`;
      reason_en = `Staff consumption: ${staffCount} staff × ${buffer.allowance_per_staff_per_day} ${buffer.unit}/day × ${periodDays} days`;
    } else if (buffer.allowance_per_shift > 0) {
      // Assume ~3 shifts per day
      qty = parseFloat(buffer.allowance_per_shift) * 3 * periodDays;
      reason_ar = `بدل وردية: ${buffer.allowance_per_shift} ${buffer.unit}/وردية × 3 × ${periodDays}`;
      reason_en = `Per-shift allowance: ${buffer.allowance_per_shift} ${buffer.unit}/shift × 3 × ${periodDays}`;
    } else if (buffer.allowance_percentage_of_usage > 0) {
      // Percentage of EXPECTED usage
      qty = (parseFloat(gap.expected_value || 0) * parseFloat(buffer.allowance_percentage_of_usage)) / 100;
      reason_ar = `هامش هدر طبيعي: ${buffer.allowance_percentage_of_usage}% من الاستهلاك المتوقع`;
      reason_en = `Natural waste tolerance: ${buffer.allowance_percentage_of_usage}% of expected`;
    }

    if (qty > 0) {
      totalAllowanceQty += qty;
      breakdown.push({
        buffer_type: buffer.buffer_type,
        allowance_qty: qty,
        reason_ar,
        reason_en
      });
    }
  }

  // Adjust gap: original gap (negative = over-consumed) + allowance deduction
  const adjustedGapQty = Math.max(0, rawGapAmount - totalAllowanceQty);
  const reductionRatio = rawGapAmount > 0 ? adjustedGapQty / rawGapAmount : 0;
  const adjustedLoss = rawLoss * reductionRatio;

  return {
    ...gap,
    raw_gap_amount: rawGapAmount,
    raw_estimated_loss: rawLoss,
    allowance_qty: Math.round(totalAllowanceQty * 100) / 100,
    adjusted_gap_amount: Math.round(adjustedGapQty * 100) / 100,
    adjusted_estimated_loss: Math.round(adjustedLoss * 100) / 100,
    buffer_breakdown: breakdown,
    // Promote "adjusted" values to top-level for display; preserve originals
    gap_amount: -Math.round(adjustedGapQty * 100) / 100,
    estimated_monetary_loss: Math.round(adjustedLoss * 100) / 100,
    buffer_applied: totalAllowanceQty > 0
  };
}

/**
 * Apply buffers across all gaps. Also downgrades severity if loss dropped.
 */
export function applyBuffersToGaps({ gaps = [], buffers = [], employees = [], periodDays = 30 }) {
  return gaps.map(gap => {
    const adjusted = applyBuffers({ gap, buffers, employees, periodDays });

    // Re-classify severity based on adjusted loss
    const newLoss = adjusted.adjusted_estimated_loss;
    let newSeverity = adjusted.severity;
    if (adjusted.buffer_applied) {
      if (newLoss < 100) newSeverity = 'low';
      else if (newLoss < 500) newSeverity = 'medium';
      else if (newLoss < 1500) newSeverity = 'high';
      else newSeverity = 'critical';
    }

    return { ...adjusted, severity: newSeverity };
  });
}
